import React, { useCallback, useMemo, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { HiCheckCircle, HiLightningBolt, HiChatAlt2 } from 'react-icons/hi';
import './WorkWithMe.css';

export default function WorkWithMe() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [status, setStatus] = useState('idle');
  const [form, setForm] = useState({
    name: '',
    email: '',
    service: 'My project is delayed / stalled',
  });

  const canSubmit = useMemo(() => {
    return (
      form.name.trim().length > 1 &&
      /\S+@\S+\.\S+/.test(form.email) &&
      form.service.trim().length > 0
    );
  }, [form.email, form.name, form.service]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!canSubmit) {
        setStatus('error');
        return;
      }

      if (!executeRecaptcha) return;

      setStatus('sending');
      await executeRecaptcha('consultation_form');

      const subject = encodeURIComponent('Consultation Request - Website Inquiry');
      const body = encodeURIComponent(
        `Hi Emmanuel,\n\nI came across your website and I'm interested in discussing a potential collaboration.\n\n` +
          `My Details:\n` +
          `Name: ${form.name}\n` +
          `Email: ${form.email}\n` +
          `Service I'm interested in: ${form.service}\n\n` +
          `I'd appreciate the opportunity to schedule a consultation to discuss my project needs in more detail.\n\n` +
          `Looking forward to connecting with you.\n\n` +
          `Best regards,\n${form.name}`
      );

      window.location.href = `mailto:me@emmanuelobengconsulting.com?subject=${subject}&body=${body}`;

      setStatus('success');
      setTimeout(() => setStatus('idle'), 5000);
    },
    [canSubmit, executeRecaptcha, form]
  );

  return (
    <section className="section workWithMe anchor" id="work-with-me">
      <div className="container workWithMe__inner">
        <div className="workWithMe__content">
          <div className="workWithMe__badge">FREE · No obligation</div>
          <h2 className="workWithMe__title">Book Your Free Delivery Audit</h2>
          <p className="workWithMe__subtitle">
            If your project is behind, your systems are unstable, or your team is stuck - tell me
            what's happening. I'll reply with the three things that need to change first.
          </p>

          <ul className="workWithMe__bullets" aria-label="What you get">
            <li>
              <HiCheckCircle className="workWithMe__bulletIcon" />
              <span>Honest diagnosis - what's actually causing the delay or failure</span>
            </li>
            <li>
              <HiLightningBolt className="workWithMe__bulletIcon" />
              <span>A prioritised fix plan you can act on immediately</span>
            </li>
            <li>
              <HiChatAlt2 className="workWithMe__bulletIcon" />
              <span>No pressure, no sales pitch - just clarity on your situation</span>
            </li>
          </ul>

          <div className="workWithMe__miniCta">
            Prefer to call?{' '}
            <a className="workWithMe__miniLink" href="tel:+233548239576">
              054 823 9576
            </a>
          </div>
        </div>

        <form className="workWithMe__form" onSubmit={onSubmit} aria-label="Lead generation form">
          <h3 className="workWithMe__formTitle">Start with a free audit</h3>
          <p className="workWithMe__formHint">Three fields. I'll respond within 24 hours.</p>

          <label className="workWithMe__field">
            <span className="workWithMe__fieldLabel">Full name</span>
            <input
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Your name"
              autoComplete="name"
              required
            />
          </label>

          <label className="workWithMe__field">
            <span className="workWithMe__fieldLabel">Email</span>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          </label>

          <label className="workWithMe__field">
            <span className="workWithMe__fieldLabel">What do you need help with?</span>
            <select name="service" value={form.service} onChange={onChange} required>
              <option>My project is delayed / stalled</option>
              <option>Project Management</option>
              <option>IT Support &amp; Infrastructure</option>
              <option>System Stability Assessment</option>
              <option>Both PM &amp; IT Support</option>
              <option>Not sure yet</option>
            </select>
          </label>

          <button
            className="workWithMe__submit"
            type="submit"
            disabled={!canSubmit || status === 'sending'}
          >
            {status === 'sending' ? 'Verifying...' : 'Book a Consultation'}
          </button>

          {status === 'success' && (
            <div className="workWithMe__status workWithMe__status--success" role="status">
              ✅ Opening your email client to send the request...
            </div>
          )}

          {status === 'error' && (
            <div className="workWithMe__status workWithMe__status--error" role="alert">
              ❌ Please fill out all fields correctly.
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
